
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MaintenanceItem, Status, Priority, UpdatePayload, Message, MaintenanceImage, Caregiver } from '../../types';
import { getPriorityFromApi } from './priorityHelper';
import StatusBadge from '../../components/StatusBadge';
import PriorityIcon from '../../components/PriorityIcon';
import { formatTimeAgo } from '../../utils/dateHelper';
import Breadcrumbs from '../../components/Breadcrumbs';
import { summarizeTaskActivity } from './aiService';
import AiSummary from './AiSummary';
import ImageUploader from './ImageUploader';
import Lightbox from './Lightbox';
import PostImageToFeedModal from './PostImageToFeedModal';
import RichTextEditor from './RichTextEditor';
import { ISSUE_CATEGORIES } from '../../constants';
import { fetchCaregivers } from './maintenanceService';

const substituteImageUrls = (htmlContent: string | null | undefined, availableImages: MaintenanceImage[]): string => {
    if (!htmlContent) return '';
    if (!availableImages || availableImages.length === 0 || !htmlContent.includes('data-image-id')) {
        return htmlContent;
    }
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, 'text/html');
        const imagesInHtml = doc.querySelectorAll('img[data-image-id]');
        imagesInHtml.forEach(imgElement => {
            const imageId = imgElement.getAttribute('data-image-id');
            if (imageId) {
                const imageIdNum = parseInt(imageId, 10);
                const correspondingImage = availableImages.find(img => img.id === imageIdNum);
                if (correspondingImage) {
                    imgElement.setAttribute('src', correspondingImage.image_url_s3);
                } else {
                    imgElement.setAttribute('src', 'https://via.placeholder.com/150?text=Image+Not+Found');
                }
            }
        });
        return doc.body.innerHTML;
    } catch (e) {
        return htmlContent;
    }
};

const tryParseAuditLog = (content: string) => {
    if (!content || !content.trim().startsWith('{')) return null;
    try {
        const parsed = JSON.parse(content);
        if (parsed && typeof parsed === 'object' && 'audit' in parsed) {
            return parsed.audit;
        }
    } catch (e) {
        // Not a JSON audit log
    }
    return null;
};

const capitalize = (s: string) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

const formatAuditMessage = (audit: any) => {
    switch (audit.type) {
        case 'status_change':
            return (
                <span>
                    changed status from <span className="font-semibold text-gray-800">{capitalize(audit.from)}</span> to <span className="font-semibold text-gray-800">{capitalize(audit.to)}</span>
                </span>
            );
        case 'priority_change':
             return (
                <span>
                    changed priority from <span className="font-semibold text-gray-800">{capitalize(audit.from)}</span> to <span className="font-semibold text-gray-800">{capitalize(audit.to)}</span>
                </span>
            );
         case 'assignee_change':
             return (
                <span>
                    assigned task to <span className="font-semibold text-gray-800">{audit.to}</span>
                </span>
            );
        default:
            return <span>made an update</span>;
    }
};

interface MaintenanceDetailsPageProps {
  item: MaintenanceItem;
  isLoadingMessages: boolean;
  onGoBack: () => void;
  onAddMessage: (id: number, message: string, imageUrl?: string) => Promise<void>;
  onUpdate: (id: number, payload: UpdatePayload) => Promise<void>;
  onRefreshData: () => void;
  currentUser: string;
}

const statusOptions = [Status.Open, Status.InProgress, Status.Completed];
const priorityOptions = [Priority.Critical, Priority.High, Priority.Medium, Priority.Low];

const statusChipStyles: { [key in Status]: { selected: string; unselected: string } } = {
    [Status.Open]:       { selected: 'bg-red-600 text-white border-red-600',    unselected: 'text-red-700 bg-white border-red-300 hover:bg-red-50 hover:border-red-500' },
    [Status.InProgress]: { selected: 'bg-yellow-500 text-white border-yellow-500', unselected: 'text-yellow-800 bg-white border-yellow-400 hover:bg-yellow-50 hover:border-yellow-600' },
    [Status.Completed]:  { selected: 'bg-green-600 text-white border-green-600',  unselected: 'text-green-800 bg-white border-green-400 hover:bg-green-50 hover:border-green-600' },
    [Status.Pending]:    { selected: 'bg-blue-600 text-white border-blue-600',   unselected: 'text-blue-800 bg-white border-blue-400 hover:bg-blue-50 hover:border-blue-600' },
};

const priorityChipStyles: { [key in Priority]: { selected: string; unselected: string } } = {
    [Priority.Critical]: { selected: 'bg-red-600 text-white border-red-600',    unselected: 'text-red-700 bg-white border-red-300 hover:bg-red-50 hover:border-red-500' },
    [Priority.High]:   { selected: 'bg-orange-500 text-white border-orange-500',    unselected: 'text-orange-700 bg-white border-orange-300 hover:bg-orange-50 hover:border-orange-500' },
    [Priority.Medium]: { selected: 'bg-yellow-500 text-white border-yellow-500', unselected: 'text-yellow-800 bg-white border-yellow-400 hover:bg-yellow-50 hover:border-yellow-600' },
    [Priority.Low]:    { selected: 'bg-green-600 text-white border-green-600',  unselected: 'text-green-800 bg-white border-green-400 hover:bg-green-50 hover:border-green-600' },
};

const MaintenanceDetailsPage: React.FC<MaintenanceDetailsPageProps> = ({ item, isLoadingMessages, onGoBack, onAddMessage, onUpdate, onRefreshData, currentUser }) => {
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const [editedTitle, setEditedTitle] = useState(item.title || '');
  const [editedDescription, setEditedDescription] = useState(item.description || '');
  const [editedStatus, setEditedStatus] = useState<Status>(item.status as Status);
  const [editedPriority, setEditedPriority] = useState<Priority>(getPriorityFromApi(item.priority));
  const [editedCategory, setEditedCategory] = useState<string>(item.issues_category || '');
  const [editedAssignedTo, setEditedAssignedTo] = useState<number>(item.assigned_to);

  const [summary, setSummary] = useState<string | null>(null);
  const [isSummarizing, setIsSummarizing] = useState<boolean>(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [imageToPost, setImageToPost] = useState<MaintenanceImage | null>(null);
  
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const categoryDropdownRef = useRef<HTMLDivElement>(null);

  const [caregivers, setCaregivers] = useState<Caregiver[]>([]);
  const [isCaregiverDropdownOpen, setIsCaregiverDropdownOpen] = useState(false);
  const [caregiverSearch, setCaregiverSearch] = useState('');
  const caregiverDropdownRef = useRef<HTMLDivElement>(null);
  const [isCaregiversLoading, setIsCaregiversLoading] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setEditedTitle(item.title || '');
      const desc = item.description || '';
      setEditedDescription(desc.startsWith('<p>') ? desc : `<p>${desc}</p>`);
      setEditedStatus(item.status as Status);
      setEditedPriority(getPriorityFromApi(item.priority));
      setEditedCategory(item.issues_category || '');
      setEditedAssignedTo(item.assigned_to);
    } else if (caregivers.length === 0) {
        loadCaregiversList();
    }
  }, [item, isEditing]);

  const loadCaregiversList = async () => {
    setIsCaregiversLoading(true);
    try {
        const data = await fetchCaregivers(item.centre_id);
        setCaregivers(data || []);
    } catch (e) {
        console.error("Failed to load caregivers:", e);
    } finally {
        setIsCaregiversLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
        if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) setIsCategoryDropdownOpen(false);
        if (caregiverDropdownRef.current && !caregiverDropdownRef.current.contains(event.target as Node)) setIsCaregiverDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentCaregiverName = useMemo(() => {
      if (editedAssignedTo === 0) return 'Unassigned';
      if (caregivers.length > 0) {
          const caregiver = caregivers.find(c => c && c.id === editedAssignedTo);
          return caregiver ? caregiver.full_name : (item.assigned_to_full_name || 'Select staff...');
      }
      return item.assigned_to_full_name || 'Select staff...';
  }, [caregivers, editedAssignedTo, item.assigned_to_full_name]);

  const filteredCaregivers = useMemo(() => {
      const search = (caregiverSearch || '').trim().toLowerCase();
      if (!search) return caregivers;
      return caregivers.filter(c => 
          c && (
            (String(c.full_name || '')).toLowerCase().includes(search) ||
            (String(c.role || '')).toLowerCase().includes(search)
          )
      );
  }, [caregivers, caregiverSearch]);

  const postedImageUrls = useMemo(() => 
      new Set((item.messages || []).map(msg => msg.image).filter(Boolean)),
      [item.messages]
  );

  const filteredImages = useMemo(() => {
      return item.images || [];
  }, [item.images]);

  const handleMessageSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!newMessage.trim() || newMessage === '<p><br></p>') return;
      setIsSending(true);
      try {
        await onAddMessage(item.id, newMessage);
        setNewMessage('');
      } catch (error) {} finally {
        setIsSending(false);
      }
  };
  
  const handleSave = async () => {
    setIsSaving(true);
    const payload: UpdatePayload = {};
    if (editedTitle.trim() !== item.title) payload.title = editedTitle.trim();
    if (editedDescription !== item.description) payload.description = editedDescription;
    if (editedStatus !== (item.status as Status)) payload.status = editedStatus;
    if (editedPriority !== getPriorityFromApi(item.priority)) payload.priority = editedPriority;
    if (editedAssignedTo !== item.assigned_to) payload.assigned_to = editedAssignedTo;
    if (editedCategory !== item.issues_category) payload.issues_category = editedCategory || null;

    if (Object.keys(payload).length > 0) await onUpdate(item.id, payload);
    setIsSaving(false);
    setIsEditing(false);
  };

  const renderActivityFeed = () => {
    if (isLoadingMessages) return <div className="text-center py-10 text-gray-500">Loading activity...</div>;
    const sorted = [...(item.messages || [])].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    
    if (sorted.length === 0) {
        return <div className="text-center py-10 text-gray-500 italic">No activity yet.</div>;
    }

    return sorted.map((msg, index) => {
        const auditLog = tryParseAuditLog(msg.message);
        const fullName = `${msg.created_by_first_name || ''} ${msg.created_by_last_name || ''}`.trim() || 'Unknown User';
        
        // Render Audit Log
        if (auditLog) {
            return (
                <div key={msg.id} className="flex items-center gap-3 py-3 pl-12 relative group">
                    <div className="absolute left-[2.25rem] top-0 bottom-0 w-px bg-gray-100 -z-10 group-first:top-1/2 group-last:bottom-1/2"></div>
                    
                    <div className="flex-shrink-0 z-10">
                         <div className="bg-gray-100 h-6 w-6 rounded-full flex items-center justify-center border border-gray-200">
                             <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                             </svg>
                         </div>
                    </div>
                    <div className="text-sm text-gray-500">
                        <span className="font-semibold text-gray-700">{fullName}</span>{' '}
                        {formatAuditMessage(auditLog)}
                        <span className="text-xs text-gray-400 ml-2" title={new Date(msg.created_at).toLocaleString()}>
                            {formatTimeAgo(msg.created_at)}
                        </span>
                    </div>
                </div>
            );
        }

        // Render Standard Message
        const initial = (msg.created_by_first_name || ' ').charAt(0).toUpperCase();
        return (
          <div key={msg.id} className="flex items-start gap-3 py-3 relative group">
            <div className="absolute left-[1.125rem] top-0 bottom-0 w-px bg-gray-100 -z-10 group-first:top-3 group-last:bottom-full"></div>

            <div className="flex-shrink-0 z-10 bg-white">
                {msg.created_by_image_url_s3 ? (
                    <img src={msg.created_by_image_url_s3} alt={fullName} className="h-9 w-9 rounded-full object-cover border border-gray-200 shadow-sm" />
                ) : (
                    <div className="bg-indigo-100 h-9 w-9 rounded-full flex items-center justify-center font-bold text-indigo-700 text-sm border border-indigo-200 shadow-sm">{initial}</div>
                )}
            </div>
            <div className="flex-grow max-w-[90%]">
              <div className="flex items-baseline justify-between mb-1">
                 <span className="font-semibold text-gray-900 text-sm">{fullName}</span>
                 <span className="text-xs text-gray-400">{formatTimeAgo(msg.created_at)}</span>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg rounded-tl-none p-3 shadow-sm text-gray-800 text-sm">
                <div className="rendered-html prose prose-sm max-w-none break-words" dangerouslySetInnerHTML={{ __html: substituteImageUrls(msg.message, item.images) }} />
                {msg.image && (
                     <div className="mt-3">
                        <a href={msg.image} target="_blank" rel="noopener noreferrer">
                             <img src={msg.image} className="max-h-48 rounded-md border border-gray-200" alt="Attachment" />
                        </a>
                     </div>
                )}
              </div>
            </div>
          </div>
        );
      });
  };

  return (
    <>
        <Breadcrumbs items={[{ label: 'Dashboard', onClick: onGoBack, disabled: isEditing }, { label: `Task #${item.id}` }]} />
        
        <header className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            {isEditing ? (
                <input type="text" value={editedTitle} onChange={(e) => setEditedTitle(e.target.value)} className="w-full px-3 py-2 text-2xl font-bold bg-indigo-50 border border-indigo-200 rounded-md text-gray-900" />
            ) : (
                <h2 className="text-2xl font-bold text-gray-900 pr-4 flex-grow">{item.title}</h2>
            )}
            <div className="flex items-center gap-3">
                {isEditing ? (
                    <>
                        <button onClick={() => setIsEditing(false)} className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 text-gray-700 shadow-sm">Cancel</button>
                        <button onClick={handleSave} disabled={isSaving} className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 shadow-sm disabled:bg-indigo-300">{isSaving ? 'Saving...' : 'Save'}</button>
                    </>
                ) : (
                    <>
                        <button 
                            onClick={async () => { setIsSummarizing(true); setSummary(null); try { setSummary(await summarizeTaskActivity(item)); } catch (e) { setSummaryError('Failed to summarize'); } finally { setIsSummarizing(false); }}} 
                            disabled={isSummarizing || !item.messages?.length} 
                            className="inline-flex items-center gap-2 px-4 py-2 border border-indigo-200 rounded-md shadow-sm text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSummarizing ? (
                                <>
                                    <svg className="animate-spin -ml-1 h-4 w-4 text-indigo-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                    Summarizing...
                                </>
                            ) : (
                                <>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                                    AI Summary
                                </>
                            )}
                        </button>
                        <button 
                            onClick={() => setIsEditing(true)} 
                            className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg>
                            Edit
                        </button>
                    </>
                )}
            </div>
        </header>

        <AiSummary summary={summary} error={summaryError} onDismiss={() => { setSummary(null); setSummaryError(null); }} />
        
        <div className="mt-6 lg:grid lg:grid-cols-3 lg:gap-x-8">
            <div className="lg:col-span-2 space-y-6">
                <div className="bg-white rounded-lg shadow border p-5">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Description</h3>
                    {isEditing ? (
                        <RichTextEditor value={editedDescription} onChange={setEditedDescription} availableImages={item.images} />
                    ) : (
                        <div className="text-gray-900 rendered-html" dangerouslySetInnerHTML={{ __html: substituteImageUrls(item.description, item.images) }} />
                    )}
                </div>

                <div className="bg-white rounded-lg shadow border p-5">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Attachments</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {filteredImages.map((image, idx) => (
                            <div key={image.id} className="relative group aspect-square rounded-lg overflow-hidden border">
                                <img src={image.image_url_s3} className="w-full h-full object-cover cursor-pointer hover:opacity-90" onClick={() => setLightboxIndex(idx)} />
                                <button
                                    onClick={() => setImageToPost(image)}
                                    className="absolute bottom-2 right-2 bg-white/90 p-1.5 rounded-full shadow-sm text-gray-600 hover:text-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                    title="Post to activity"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                    </svg>
                                </button>
                            </div>
                        ))}
                    </div>
                    {!isEditing && <div className="mt-4"><ImageUploader maintenanceId={item.id} onUploadComplete={onRefreshData} userId={currentUser} /></div>}
                </div>

                <div className="bg-white rounded-lg shadow border p-5">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Activity</h3>
                    <div className="space-y-1">{renderActivityFeed()}</div>
                    <div className="mt-6 pt-4 border-t">
                        <form onSubmit={handleMessageSubmit} className="flex gap-3">
                            <div className="flex-grow">
                                <RichTextEditor value={newMessage} onChange={setNewMessage} availableImages={item.images} placeholder="Add a comment..." disabled={isSending || isEditing} />
                            </div>
                            <button type="submit" disabled={isSending || isEditing || !newMessage.trim()} className="px-4 py-2 bg-indigo-600 text-white rounded-md self-end hover:bg-indigo-700 disabled:bg-indigo-300">Send</button>
                        </form>
                    </div>
                </div>
            </div>

            <div className="lg:col-span-1 space-y-6">
                <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
                    <dl className="space-y-6">
                        {
                            item.centre_id == 6 &&
                            <div>
                                <dt className="text-sm text-gray-500 mb-2">Order ID</dt>
                                <dd>
                                    {item?.order_id}
                                </dd>
                            </div>
                        }
                        <div>
                            <dt className="text-sm text-gray-500 mb-2">Status</dt>
                            <dd>
                                {isEditing ? (
                                    <div className="flex flex-wrap gap-2">
                                        {statusOptions.map(s => <button key={s} onClick={() => setEditedStatus(s)} className={`px-3 py-1 rounded-full text-xs font-medium border-2 ${editedStatus === s ? statusChipStyles[s].selected : statusChipStyles[s].unselected}`}>{s}</button>)}
                                    </div>
                                ) : <StatusBadge status={item.status as Status} />}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm text-gray-500 mb-2">Priority</dt>
                            <dd className="flex items-center gap-2">
                                {isEditing ? (
                                    <div className="flex flex-wrap gap-2">
                                        {priorityOptions.map(p => <button key={p} onClick={() => setEditedPriority(p)} className={`px-3 py-1 rounded-full text-xs font-medium border-2 ${editedPriority === p ? priorityChipStyles[p].selected : priorityChipStyles[p].unselected}`}>{p}</button>)}
                                    </div>
                                ) : (
                                    <>
                                        <PriorityIcon priority={getPriorityFromApi(item.priority)} />
                                        <span className="font-medium text-gray-900">{getPriorityFromApi(item.priority)}</span>
                                    </>
                                )}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm text-gray-500 mb-2">Category</dt>
                            <dd>
                                {isEditing ? (
                                    <div className="relative" ref={categoryDropdownRef}>
                                        <button onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)} className="w-full px-3 py-2 text-left bg-white border border-gray-300 rounded-md text-sm flex justify-between items-center text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                                            <span className="truncate">{editedCategory ? ISSUE_CATEGORIES[editedCategory] || editedCategory : 'Select Category...'}</span>
                                            <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                                        </button>
                                        {isCategoryDropdownOpen && (
                                            <div className="absolute z-20 w-full mt-1 bg-white border rounded-md shadow-lg">
                                                <div className="p-2 border-b"><input type="text" placeholder="Search category..." value={categorySearch} onChange={(e) => setCategorySearch(e.target.value)} className="w-full px-2 py-1 text-sm border border-gray-300 rounded text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500" autoFocus /></div>
                                                <ul className="max-h-48 overflow-y-auto">
                                                    <li onClick={() => { setEditedCategory(''); setIsCategoryDropdownOpen(false); }} className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm italic text-gray-500">Uncategorized</li>
                                                    {Object.entries(ISSUE_CATEGORIES).filter(([k, v]) => v.toLowerCase().includes(categorySearch.toLowerCase())).map(([key, value]) => (
                                                        <li key={key} onClick={() => { setEditedCategory(key); setIsCategoryDropdownOpen(false); }} className="px-3 py-2 hover:bg-indigo-50 cursor-pointer text-sm text-gray-700 hover:text-gray-900">{value}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                ) : <span className="font-medium text-gray-900">{item.issues_category ? ISSUE_CATEGORIES[item.issues_category] || item.issues_category : 'Uncategorized'}</span>}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm text-gray-500 mb-2">Assigned To</dt>
                            <dd>
                                {isEditing ? (
                                    <div className="relative" ref={caregiverDropdownRef}>
                                        <button onClick={() => setIsCaregiverDropdownOpen(!isCaregiverDropdownOpen)} className="w-full px-3 py-2 text-left bg-white border border-gray-300 rounded-md text-sm flex justify-between items-center text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                                            <span className="truncate">{currentCaregiverName}</span>
                                            <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                                        </button>
                                        {isCaregiverDropdownOpen && (
                                            <div className="absolute z-20 w-full mt-1 bg-white border rounded-md shadow-lg">
                                                <div className="p-2 border-b"><input type="text" placeholder="Search staff..." value={caregiverSearch} onChange={(e) => setCaregiverSearch(e.target.value)} className="w-full px-2 py-1 text-sm border border-gray-300 rounded text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500" autoFocus /></div>
                                                <ul className="max-h-48 overflow-y-auto">
                                                    {isCaregiversLoading ? <li className="px-3 py-2 text-xs italic text-gray-500">Loading...</li> : (
                                                        <>
                                                            <li onClick={() => { setEditedAssignedTo(0); setIsCaregiverDropdownOpen(false); }} className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm font-semibold text-red-600">Unassign</li>
                                                            {filteredCaregivers.map(c => (
                                                                <li key={c.id} onClick={() => { setEditedAssignedTo(c.id); setIsCaregiverDropdownOpen(false); }} className="px-3 py-2 hover:bg-indigo-50 cursor-pointer border-b last:border-0">
                                                                    <div className="text-sm font-bold text-gray-900">{c.full_name}</div>
                                                                    <div className="text-[10px] text-gray-500 uppercase">{c.role}</div>
                                                                </li>
                                                            ))}
                                                        </>
                                                    )}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                ) : <span className="font-medium text-gray-900">{item.assigned_to_full_name || 'Unassigned'}</span>}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm text-gray-500 mb-1">Reported By</dt>
                            <dd className="font-medium text-gray-900">{item.created_by_full_name}</dd>
                        </div>
                        <div>
                            <dt className="text-sm text-gray-500 mb-1">Created</dt>
                            <dd className="font-medium text-gray-900">{new Date(item.created_at).toLocaleString()}</dd>
                        </div>
                    </dl>
                </div>
            </div>
        </div>

        {lightboxIndex !== null && <Lightbox images={item.images} startIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} />}
        {imageToPost && <PostImageToFeedModal image={imageToPost} onClose={() => setImageToPost(null)} onPost={async (c) => { await onAddMessage(item.id, c || '[Image]', imageToPost.image_url_s3); }} />}
    </>
  );
};

export default MaintenanceDetailsPage;
